/**
 * EntityToolsPanel — the maintenance editors an analyst needs *while*
 * reading an analysis, in one floating window.
 *
 * Deliberately NOT a modal: there is no backdrop, so the analysis page
 * behind it stays readable and scrollable. That is the whole point — the
 * analyst reads field names in the SQL Investigation results and types
 * them into the entity/module editors without leaving the page. Before
 * this, fixing a missing entity type meant leaving Issue Analysis, opening
 * Doc Processing, expanding Metadata Configuration, and finding the editor.
 *
 * Drag it by the title bar, resize from the bottom-right grip; both are
 * remembered. The four editors are the SAME components Doc Processing
 * renders (each is zero-prop and self-fetching), so anything saved here
 * behaves exactly as it does there — and switching tabs remounts them,
 * which is what keeps a just-added entity type visible in the others.
 *
 * Owned by AnalysisResult, not by the JIRA tab: an inactive tab unmounts,
 * which would take this window and any half-typed form down with it the
 * moment the analyst switched tabs to read something.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, GripVertical } from 'lucide-react';

import EntityTypeEditor from '../doc-processing/EntityTypeEditor';
import ModuleGroupingEditor from '../doc-processing/ModuleGroupingEditor';
import MasterLookupsEditor from '../doc-processing/MasterLookupsEditor';
import ModuleLinkStoplistEditor from '../doc-processing/ModuleLinkStoplistEditor';
import { SchemaNodeListPanel } from '../schema-graph/SchemaNodeListPanel';
import { useSchemaGraphData } from '../schema-graph/graphLoader';

type TabId = 'entity-types' | 'groupings' | 'lookups' | 'stoplist' | 'schema';

const TABS: { id: TabId; label: string }[] = [
  { id: 'entity-types', label: 'Entity Types' },
  { id: 'groupings', label: 'Module Groupings' },
  { id: 'lookups', label: 'Master Lookup' },
  { id: 'stoplist', label: 'Module Stoplist' },
  { id: 'schema', label: 'Schema Browser' },
];

const GEOMETRY_KEY = 'lisa-entity-tools-geometry';
const MIN_W = 520;
const MIN_H = 320;

interface Geometry { x: number; y: number; w: number; h: number }

function defaultGeometry(): Geometry {
  const w = Math.min(1180, Math.max(MIN_W, window.innerWidth - 120));
  const h = Math.min(760, Math.max(MIN_H, window.innerHeight - 140));
  return { x: Math.max(12, (window.innerWidth - w) / 2), y: 64, w, h };
}

/** Keep the window on screen after a resize of the browser or a stale
 *  saved position from a bigger monitor. */
function clampGeometry(g: Geometry): Geometry {
  const w = Math.min(Math.max(g.w, MIN_W), window.innerWidth - 24);
  const h = Math.min(Math.max(g.h, MIN_H), window.innerHeight - 24);
  return {
    w,
    h,
    x: Math.min(Math.max(g.x, -w + 160), window.innerWidth - 160),
    y: Math.min(Math.max(g.y, 0), window.innerHeight - 60),
  };
}

function loadGeometry(): Geometry {
  try {
    const raw = localStorage.getItem(GEOMETRY_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<Geometry>;
      if (
        typeof parsed.x === 'number' && typeof parsed.y === 'number' &&
        typeof parsed.w === 'number' && typeof parsed.h === 'number'
      ) {
        return clampGeometry(parsed as Geometry);
      }
    }
  } catch {
    // Private windows and cleared site data both land here — fall through.
  }
  return defaultGeometry();
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Selected table FQNs from the Schema Browser tab, to become starter
   *  SELECT cards in SQL Investigation. */
  onAddTables: (fqns: string[]) => void;
}

export function EntityToolsPanel({ open, onClose, onAddTables }: Props) {
  const [active, setActive] = useState<TabId>('entity-types');
  const [geo, setGeo] = useState<Geometry>(loadGeometry);
  const dragRef = useRef<{ mode: 'move' | 'resize'; startX: number; startY: number; start: Geometry } | null>(null);

  // Re-clamp when the window opens AND whenever the viewport changes —
  // otherwise shrinking the browser can strand the title bar off-screen
  // with no way to drag it back.
  useEffect(() => {
    if (!open) return;
    setGeo((g) => clampGeometry(g));
    const onResize = () => setGeo((g) => clampGeometry(g));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Escape inside a field means "dismiss the autocomplete", not "throw
      // away everything I typed". Only close from non-editable targets.
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Each activation remounts the editor, so it refetches: add an entity
  // type here and the Master Lookup tab's type list has it immediately.
  const selectTab = useCallback((id: TabId) => setActive(id), []);

  const beginDrag = useCallback((mode: 'move' | 'resize') => (e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = { mode, startX: e.clientX, startY: e.clientY, start: geo };
    const onMove = (ev: MouseEvent) => {
      const d = dragRef.current;
      if (!d) return;
      // Released outside the viewport (over browser chrome or another
      // screen)? No mouseup ever arrives, so detect the empty button mask
      // instead — otherwise the window keeps following the cursor.
      if (ev.buttons === 0) { onUp(); return; }
      const dx = ev.clientX - d.startX;
      const dy = ev.clientY - d.startY;
      setGeo(clampGeometry(
        d.mode === 'move'
          ? { ...d.start, x: d.start.x + dx, y: d.start.y + dy }
          : { ...d.start, w: d.start.w + dx, h: d.start.h + dy },
      ));
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      // Persist once at the end of the gesture, not per pixel.
      setGeo((g) => {
        try { localStorage.setItem(GEOMETRY_KEY, JSON.stringify(g)); } catch { /* non-fatal */ }
        return g;
      });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [geo]);

  if (!open) return null;

  // Portalled to <body> for three reasons: a fixed window must not be
  // clipped or re-anchored by an ancestor's transform/overflow; its
  // z-index must not be trapped in a nested stacking context; and the
  // JIRA tab wraps its subtree in HintCaptureMenu, which pops a
  // "capture this as a hint" menu for any selection inside that subtree —
  // selecting a table name in here would have triggered it.
  //
  // zIndex 45 is deliberate: above the page, but BELOW the app's modal
  // dialogs (z-50), so a flowchart or "Browse all" opened from behind
  // lands on top of this window rather than under it.
  return createPortal(
    <div
      role="dialog"
      aria-label="Entity Types and module maintenance"
      className="fixed flex flex-col bg-white rounded-lg shadow-2xl border border-gray-300 overflow-hidden"
      style={{ left: geo.x, top: geo.y, width: geo.w, height: geo.h, zIndex: 45 }}
    >
      {/* Title bar — the drag handle */}
      <div
        onMouseDown={beginDrag('move')}
        className="flex items-center gap-2 px-3 py-2 bg-gray-100 border-b border-gray-300 cursor-move select-none flex-shrink-0"
      >
        <GripVertical size={14} className="text-gray-400" />
        <span className="text-sm font-semibold text-gray-800">Entity Types &amp; Module Setup</span>
        <span className="text-[11px] text-gray-500 hidden sm:inline">
          — drag to move; the analysis stays readable behind
        </span>
        <button
          onClick={onClose}
          onMouseDown={(e) => e.stopPropagation()}
          className="ml-auto p-1 rounded hover:bg-gray-200 text-gray-600"
          title="Close (Esc)"
          aria-label="Close"
        >
          <X size={16} />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 px-2 pt-2 bg-gray-50 border-b border-gray-200 flex-shrink-0 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => selectTab(t.id)}
            className={`px-3 py-1.5 text-xs font-medium rounded-t whitespace-nowrap transition-colors ${
              active === t.id
                ? 'bg-white text-primary-700 border border-b-white border-gray-200'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
            aria-current={active === t.id}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Body — only the active tab is mounted. Each editor fetches on
          mount, so switching tabs always shows current data (an entity
          type added a moment ago appears in the Master Lookup lists). */}
      <div className="flex-1 min-h-0 relative">
        <div className={active === 'schema' ? 'absolute inset-0' : 'absolute inset-0 overflow-y-auto p-4'}>
          {active === 'entity-types' && <EntityTypeEditor />}
          {active === 'groupings' && <ModuleGroupingEditor />}
          {active === 'lookups' && <MasterLookupsEditor />}
          {active === 'stoplist' && <ModuleLinkStoplistEditor />}
          {active === 'schema' && <SchemaTab onAddTables={onAddTables} />}
        </div>
      </div>

      {/* Resize grip */}
      <div
        onMouseDown={beginDrag('resize')}
        title="Drag to resize"
        style={{
          position: 'absolute', right: 0, bottom: 0, width: 16, height: 16,
          cursor: 'nwse-resize',
          background: 'linear-gradient(135deg, transparent 50%, #9ca3af 50%, #9ca3af 60%, transparent 60%, transparent 72%, #9ca3af 72%, #9ca3af 82%, transparent 82%)',
        }}
      />
    </div>,
    document.body,
  );
}

/** Schema Browser tab: pick tables, send them to SQL Investigation as
 *  starter cards. Same data hook and selection panel the standalone
 *  Schema Browser modal uses. */
function SchemaTab({ onAddTables }: { onAddTables: (fqns: string[]) => void }) {
  const { graphData, documents, loading, error } = useSchemaGraphData();
  const [selectedFqns, setSelectedFqns] = useState<Set<string>>(new Set());
  const [added, setAdded] = useState(0);

  const toggleSelection = useCallback((fqn: string) => {
    setSelectedFqns((prev) => {
      const next = new Set(prev);
      if (next.has(fqn)) next.delete(fqn);
      else next.add(fqn);
      return next;
    });
  }, []);

  const confirm = useCallback(() => {
    if (selectedFqns.size === 0) return;
    onAddTables(Array.from(selectedFqns));
    setAdded(selectedFqns.size);
    setSelectedFqns(new Set());
    setTimeout(() => setAdded(0), 2500);
  }, [selectedFqns, onAddTables]);

  const count = selectedFqns.size;

  const footer = (
    <div className="flex items-center justify-between gap-3 px-3 py-2 bg-gray-50">
      <span className="text-[11px] text-gray-700">
        {added > 0
          ? `Added ${added} card${added === 1 ? '' : 's'} to SQL Investigation — close this window to run them.`
          : count === 0
            ? 'Tick rows to select tables — multiple OK.'
            : `${count} table${count === 1 ? '' : 's'} selected`}
      </span>
      <button
        type="button"
        onClick={confirm}
        disabled={count === 0}
        className="px-3 py-1.5 text-[11px] font-semibold rounded bg-primary-600 text-white disabled:opacity-40 hover:bg-primary-500 transition-colors"
      >
        + Add {count > 0 ? count : ''} card{count === 1 ? '' : 's'}
      </button>
    </div>
  );

  if (loading) {
    return <div className="p-6 text-center text-xs text-gray-500">Loading schema…</div>;
  }
  if (error) {
    return (
      <div className="p-4">
        <div className="p-3 text-xs text-red-800 bg-red-50 border border-red-200 rounded">
          Failed to load schema: {error}
        </div>
      </div>
    );
  }
  return (
    <SchemaNodeListPanel
      open
      onClose={() => { /* the window's own × closes this */ }}
      graphData={graphData}
      documents={documents}
      embedded
      selectionMode="rows"
      selectedFqns={selectedFqns}
      onToggleSelection={toggleSelection}
      footerSlot={footer}
    />
  );
}

export default EntityToolsPanel;
